import React, { FC, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconDownload, IconQrCode } from '@justeattakeaway/pie-icons-react';
import PIEText from '@src/shared/components/PIEText';
import PIEList from '@src/shared/components/PIEList';
import PIEDropdownDialog from '@src/shared/components/PIEDropdownDialog';
import DeliveryDashboardBottomSheet from '@src/pages/DeliveryDashboard/components/DeliveryDashboardBottomSheet';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import styles from './QRCodeOptionsSelector.module.scss';

interface IQRCodeOptionsSelectorProps {
  onShowQRCode: () => void;
  onDownloadQRCode: () => void;
  renderToggle: (props: { onClick: () => void; isOpen: boolean }) => ReactNode;
  qaSelector?: string;
}

export const QRCodeOptionsSelector: FC<IQRCodeOptionsSelectorProps> = ({
  onShowQRCode,
  onDownloadQRCode,
  renderToggle,
  qaSelector,
}) => {
  const { t } = useTranslation();
  const { isResolutionMobile } = useDeviceDetect();
  const [showQROptions, setShowQROptions] = useState(false);

  const handleToggle = () => {
    setShowQROptions(!showQROptions);
  };

  const handleClose = () => {
    setShowQROptions(false);
  };

  const handleShowQRCode = () => {
    setShowQROptions(false);
    onShowQRCode();
  };

  const handleDownloadQRCode = () => {
    setShowQROptions(false);
    onDownloadQRCode();
  };

  const renderQRCodeOptions = () => {
    return (
      <PIEList modBorder={true}>
        <PIEList.Item
          left={<IconQrCode size="s" />}
          role={!isResolutionMobile ? 'option' : 'menuitem'}
          onClick={handleShowQRCode}
        >
          <div
            id="show-qr-code-selection"
            className={!isResolutionMobile ? styles.QRCodeSelectionContainer : ''}
          >
            <PIEText
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
              text={t('ocf_screens.COURIER_HEADER_SHOW_QR')}
            />
            <PIEText
              size="var(--xds-font-size-14)"
              fontWeight="var(--xds-font-weight-regular)"
              text={t('ocf_screens.COURIER_HEADER_SHOW_QR_DESC')}
            />
          </div>
        </PIEList.Item>
        <PIEList.Item
          onClick={handleDownloadQRCode}
          left={<IconDownload size="s" />}
          role={!isResolutionMobile ? 'option' : 'menuitem'}
        >
          <div
            id="download-qr-code-selection"
            className={!isResolutionMobile ? styles.QRCodeSelectionContainer : ''}
          >
            <PIEText
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-bold)"
              text={t('ocf_screens.COURIER_HEADER_DOWNLOAD_QR')}
            />
            <PIEText
              size="var(--xds-font-size-14)"
              fontWeight="var(--xds-font-weight-regular)"
              text={t('ocf_screens.COURIER_HEADER_DOWNLOAD_QR_DESC')}
            />
          </div>
        </PIEList.Item>
      </PIEList>
    );
  };

  // Desktop version - uses PIEDropdownDialog
  if (!isResolutionMobile) {
    return (
      <PIEDropdownDialog
        isOpen={showQROptions}
        onClose={handleClose}
        modTopOffset
        toggle={renderToggle({ onClick: handleToggle, isOpen: showQROptions })}
        qaSelector={qaSelector}
      >
        {renderQRCodeOptions()}
      </PIEDropdownDialog>
    );
  }

  // Mobile version - uses BottomSheet
  return (
    <>
      {renderToggle({ onClick: handleToggle, isOpen: showQROptions })}
      {showQROptions && (
        <DeliveryDashboardBottomSheet
          blocking
          header={
            <PIEText
              size="var(--xds-font-size-20)"
              align="left"
              text={t('ocf_screens.COURIER_HEADER_QR_OPTIONS')}
              fontWeight="var(--xds-font-weight-bold)"
            />
          }
          onBottomSheetHeight={() => {}}
          initialSnap={() => 270}
          showCloseButton
          constantHeight={270}
          onClose={handleClose}
        >
          {renderQRCodeOptions()}
        </DeliveryDashboardBottomSheet>
      )}
    </>
  );
};

export default QRCodeOptionsSelector;
